import { ComponentType, ReactNode, memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowData,
  type MRT_Row,
} from 'material-react-table';
import { Trash2, Save } from 'lucide-react';
import Button from './Button';
import { Modal } from './Modal';
import { ConfirmationModal } from './ConfirmationModal';
import { useEditableTable } from '@/hooks/useEditableTable';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/error';

/**
 * Persistent Edit Cell Component
 * Maintains its own state to prevent value reset on re-render
 */
const EditCellComponent = memo(({
  EditComp,
  initialValue,
  onValueChange,
  validationError,
  validate,
  cellKey,
}: {
  EditComp: ComponentType<any>;
  initialValue: any;
  onValueChange: (value: any) => void;
  validationError?: string;
  validate?: (value: any) => string | undefined;
  cellKey: string;
}) => {
  // Use a ref to track if value has been changed by user
  const valueRef = useRef(initialValue);
  const [localValue, setLocalValue] = useState(initialValue);

  // Update local value when cellKey or initialValue changes
  // cellKey changes when row data is refreshed (e.g., after save)
  // initialValue changes when editedRows Map is updated
  useEffect(() => {
    valueRef.current = initialValue;
    setLocalValue(initialValue);
  }, [cellKey, initialValue]);

  return (
    <EditComp
      value={localValue}
      onChange={(value: any) => {
        setLocalValue(value);
        valueRef.current = value;
        onValueChange(value);
      }}
      onBlur={() => {
        if (validate) {
          validate(localValue);
        }
      }}
      error={validationError}
    />
  );
});

/**
 * Extended column definition for editable table
 */
export interface EditableColumn<T extends MRT_RowData> extends Omit<MRT_ColumnDef<T>, 'accessorKey' | 'header'> {
  /** Unique key for the column (maps to accessorKey in MRT) */
  key: string;
  /** Header label to display */
  header: string | ReactNode;
  /** Whether this column is editable */
  editable?: boolean;
  enableSorting?: boolean;
  /** Validation function for this column */
  validate?: (value: any) => string | undefined;
  /** Custom edit component */
  EditComponent?: ComponentType<{
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
    error?: string;
  }>;
  /** Optional custom className for the column cells (for compatibility) */
  className?: string;
  /** Optional custom className for the header cell (for compatibility) */
  headerClassName?: string;
  /** Custom render function for cell content (for compatibility) */
  render?: (row: T, index: number) => ReactNode;
  /** If render is not provided, use this accessor to get the value from row */
  accessor?: keyof T | ((row: T) => ReactNode);
}

/**
 * Props for the EditableDataTable component
 * 
 * @remarks
 * **Important Limitation**: This component currently assumes that all data rows have an 'id' property.
 * The bulk update functionality extracts `row.id` to identify rows for updates.
 * 
 * For truly generic usage with custom ID fields, you should:
 * - Ensure your data type T includes an 'id' property, OR
 * - Provide a keyExtractor that returns the ID field, AND
 * - Handle the ID mapping in your onBulkUpdate callback
 * 
 * @example
 * ```tsx
 * // Your data must have an 'id' field:
 * interface MyData {
 *   id: number;
 *   name: string;
 *   // ... other fields
 * }
 * 
 * <EditableDataTable<MyData>
 *   data={myData}
 *   keyExtractor={(row) => row.id.toString()}
 *   onBulkUpdate={async (updates) => {
 *     // updates is Array<{ id: number, data: Partial<MyData> }>
 *     return await api.bulkUpdate(updates);
 *   }}
 * />
 * ```
 */
export interface EditableDataTableProps<T extends MRT_RowData> {
  /** Array of column definitions */
  columns: EditableColumn<T>[];
  /** Data rows to display (must include an 'id' property for bulk updates) */
  data: T[];
  /** Optional className for the table wrapper */
  className?: string;
  /** Optional unique key extractor function (for backward compatibility) */
  keyExtractor?: (row: T, index: number) => string | number;
  /** Enable editing functionality */
  enableEditing?: boolean;
  /** Callback when data is updated */
  onDataUpdate?: (updatedData: T[]) => void;
  /** Callback when a single row is updated */
  onRowUpdate?: (oldRow: T, newRow: T) => Promise<void>;
  /** 
   * Callback for bulk update (multiple rows at once).
   * Note: The 'id' field in updates comes from row.id (assumes data has an 'id' property)
   */
  onBulkUpdate?: (updates: Array<{ id: any; data: Partial<T> }>) => Promise<T[]>;
  /** Callback when a row is created */
  onRowCreate?: (row: T) => Promise<void>;
  /** Optional custom header actions */
  renderHeaderActions?: () => ReactNode;
  /** Callback when a row is deleted */
  onRowDelete?: (oldRow: T) => Promise<void>;
  /** Enable row deletion */
  enableDelete?: boolean;
  /** Table identifier for edit mode tracking */
  tableName?: string;
  /** Initial editing state */
  initialEditingMode?: 'cell' | 'row' | 'table' | 'modal';
  /** Table title */
  title?: string;

  onGlobalSearch?: (query: string) => Promise<void>;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  loading?: boolean;

  globalFilterValue?: string;
  /** Custom row actions render function */
  renderCustomRowActions?: (row: T) => ReactNode;
  /** Render function for expandable row detail panel */
  renderDetailPanel?: (row: T) => ReactNode;
  /** Callback when a row is expanded or collapsed */
  onExpandedChange?: (row: T, isExpanded: boolean) => void;
  /** Force manual pagination even if onPaginationChange is not provided */
  manualPagination?: boolean;
  /** Force manual sorting even if onSortingChange is not provided */
  manualSorting?: boolean;
  /** External sorting state */
  sorting?: Array<{ id: string; desc: boolean }>;
  /** Callback when sorting changes */
  onSortingChange?: (sorting: Array<{ id: string; desc: boolean }>) => void;
}

/**
 * Generic Editable DataTable component
 * Uses Material React Table for advanced editing capabilities
 */
function EditableDataTableComponent<T extends MRT_RowData>({
  columns,
  data,
  className = '',
  keyExtractor,
  enableEditing = true,
  onDataUpdate,
  onRowUpdate,
  onBulkUpdate,
  onRowCreate,
  onRowDelete,
  renderHeaderActions,

  enableDelete = false,
  initialEditingMode = 'cell',
  title,
  tableName = 'default_table',
  onGlobalSearch,
  pagination,
  onPaginationChange,
  loading,
  globalFilterValue,
  sorting,
  onSortingChange,
  manualSorting,
  manualPagination,
  renderCustomRowActions,
  renderDetailPanel,
  onExpandedChange,
}: EditableDataTableProps<T>) {
  const { addToast } = useToast();

  // Track edited rows - Map<rowId, editedData>
  const [editedRows, setEditedRows] = useState<Map<string, T>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  // Force re-render when editedRows changes
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // Track data version to force EditCellComponent remount when data refreshes
  const [dataVersion, setDataVersion] = useState(0);

  // Update data version when data changes (after save/refetch)
  useEffect(() => {
    setDataVersion(prev => prev + 1);
  }, [data]);

  // Use the generic hook for all business logic
  const {
    tableData,
    validationErrors,
    deleteModalOpen,
    isDeleting,
    editModalOpen,
    rowToEdit,
    handleCreateRow,
    handleDeleteRow,
    confirmDelete,
    cancelDelete,
    closeEditModal,
    setValidationErrors,
  } = useEditableTable(data, columns, { tableName, enableEditing, initialEditingMode, title, className }, {
    onDataUpdate,
    onRowUpdate,
    onRowCreate,
    onRowDelete,
  });

  // Determine if editing should be enabled
  const isEditingEnabled = enableEditing;

  // Get row ID helper
  const getRowId = useCallback((row: T, index: number): string => {
    if (keyExtractor) {
      const id = keyExtractor(row, index);
      // Handle undefined, null, or other falsy values
      if (id == null) {
        return index.toString();
      }
      return typeof id === 'string' ? id : String(id);
    }
    return index.toString();
  }, [keyExtractor]);

  // Handle saving all edited rows with bulk update
  const handleSaveAllChanges = useCallback(async () => {
    if (editedRows.size === 0) return;

    setIsSaving(true);

    try {
      // Convert Map to array of update objects
      const updates: Array<{ id: any; data: Partial<T> }> = [];

      for (const [rowId, editedData] of editedRows.entries()) {
        // Find original row
        const originalRowIndex = tableData.findIndex((row, idx) => getRowId(row, idx) === rowId);
        if (originalRowIndex === -1) continue;

        const originalRow = tableData[originalRowIndex];

        // Calculate changed fields only
        const changedFields: Partial<T> = {};
        for (const key in editedData) {
          if (editedData[key] !== (originalRow as any)[key]) {
            changedFields[key as keyof T] = editedData[key];
          }
        }

        // Add to updates array if there are changes
        if (Object.keys(changedFields).length > 0) {
          // NOTE: This assumes the row has an 'id' property
          // For truly generic usage, consider passing the entire originalRow to the callback
          updates.push({
            id: (originalRow as any).id,
            data: changedFields,
          });
        }
      }

      if (updates.length === 0) {
        addToast('No changes to save', 'info');
        setEditedRows(new Map());
        setIsSaving(false);
        return;
      }

      // Use bulk update API if available, otherwise fall back to sequential updates
      if (onBulkUpdate) {
        try {
          await onBulkUpdate(updates);
          // Clear edited rows and force data version update
          setEditedRows(new Map());
          setDataVersion(prev => prev + 1);
          addToast(`Successfully saved ${updates.length} row(s)`, 'success');
        } catch (error: unknown) {
          console.error('Bulk update failed:', error);
          addToast(getErrorMessage(error), 'error');
        }
      } else if (onRowUpdate) {
        // Fallback to sequential updates
        const errors: string[] = [];
        for (const update of updates) {
          try {
            // NOTE: This assumes the row has an 'id' property
            const originalRow = tableData.find((row: any) => row.id === update.id);
            if (originalRow) {
              await onRowUpdate(originalRow, { ...originalRow, ...update.data } as T);
            }
          } catch (error: unknown) {
            console.error(`Failed to save row ${update.id}:`, error);
            errors.push(`Row ${update.id}: ${getErrorMessage(error)}`);
          }
        }

        // Clear edited rows if all succeeded and force data version update
        if (errors.length === 0) {
          setEditedRows(new Map());
          setDataVersion(prev => prev + 1);
          addToast(`Successfully saved ${updates.length} row(s)`, 'success');
        } else {
          addToast(`Some changes failed: ${errors.join(', ')}`, 'error');
        }
      }
    } catch (error: unknown) {
      console.error('Failed to save changes:', error);
      addToast(getErrorMessage(error), 'error');
    } finally {
      setIsSaving(false);
    }
  }, [editedRows, tableData, onRowUpdate, onBulkUpdate, getRowId, addToast]);


  // Convert columns to MRT format
  const mrtColumns = useMemo<MRT_ColumnDef<T>[]>(
    () =>
      columns
        .filter((column) => {
          // Filter out legacy actions column when using MRT row actions
          if (column.key === 'actions') {
            return false; // Always filter out the legacy actions column
          }
          return true;
        })
        .map((column) => {
          const mrtColumn: MRT_ColumnDef<T> = {
            ...column,
            accessorKey: column.key,
            header: column.header as any,
            enableEditing: column.editable !== false && isEditingEnabled,
            enableSorting: column.enableSorting !== false && column.key !== 'actions', // Enable sorting based on column config
            muiEditTextFieldProps: {
              error: !!validationErrors[column.key],
              helperText: validationErrors[column.key],
              size: 'small',
            },
          };

          // Handle legacy accessor property
          if (column.accessor) {
            if (typeof column.accessor === 'function') {
              mrtColumn.accessorFn = column.accessor;
            } else {
              mrtColumn.accessorKey = column.accessor as string;
            }
          }

          // Handle legacy render property (excluding actions since it's filtered out)
          if (column.render) {
            mrtColumn.Cell = ({ row }) => column.render!(row.original, row.index);
          }

          // Handle custom EditComponent
          if (column.EditComponent) {
            // If no custom render function, create a Cell renderer that shows edited values
            if (!column.render) {
              mrtColumn.Cell = ({ cell, row }) => {
                const rowId = getRowId(row.original, row.index);
                const columnId = column.key;
                const editedRow = editedRows.get(rowId);
                const displayValue = editedRow?.[columnId as keyof T] ?? cell.getValue();
                return <span>{displayValue != null ? String(displayValue) : ''}</span>;
              };
            }
            (mrtColumn as any).Edit = ({ cell, column: mrtCol, row }: any) => {
              const EditComp = column.EditComponent;
              if (!EditComp) return null;

              const rowId = row.id;
              const columnId = mrtCol.id;

              // Get current value from editedRows if exists, otherwise from cell
              const editedRow = editedRows.get(rowId);
              const currentValue = editedRow?.[columnId as keyof T] ?? cell.getValue();

              return (
                <EditCellComponent
                  key={`${rowId}-${columnId}-${dataVersion}`}
                  EditComp={EditComp}
                  initialValue={currentValue}
                  cellKey={`${rowId}-${dataVersion}`}
                  onValueChange={(value: any) => {
                    // Track the change in editedRows
                    setEditedRows((prev: Map<string, T>) => {
                      const newMap = new Map(prev);
                      const existingRow = newMap.get(rowId) || { ...row.original };
                      newMap.set(rowId, { ...existingRow, [columnId]: value });
                      return newMap;
                    });

                    // Force re-render to show Save All button
                    forceUpdate();

                    // Validate on change
                    if (column.validate) {
                      const error = column.validate(value);
                      setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        if (error) {
                          newErrors[mrtCol.id as string] = error;
                        } else {
                          delete newErrors[mrtCol.id as string];
                        }
                        return newErrors;
                      });
                    }
                  }}
                  validationError={validationErrors[mrtCol.id as string]}
                  validate={column.validate}
                />
              );
            };
          }

          return mrtColumn;
        }),
    [columns, isEditingEnabled, validationErrors, setValidationErrors, setEditedRows, forceUpdate, editedRows, getRowId, dataVersion]
  );

  // Check if a row has unsaved changes
  const hasUnsavedChanges = useCallback((row: MRT_Row<T>): boolean => {
    const rowId = row.id;
    return editedRows.has(rowId);
  }, [editedRows]);

  // Table configuration
  const table = useMaterialReactTable({
    columns: mrtColumns,
    data,
    enableEditing: isEditingEnabled,
    getRowId: (originalRow, index) => getRowId(originalRow, index),
    editDisplayMode: 'cell', // Use cell mode to allow clicking any cell to edit
    createDisplayMode: 'row',
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: true,
    manualPagination: manualPagination ?? !!onPaginationChange,
    manualFiltering: !!onGlobalSearch,
    enableSorting: true,
    manualSorting: manualSorting ?? !!onSortingChange,
    enableSortingRemoval: true,
    enableBottomToolbar: true,
    enableTopToolbar: true,
    enableColumnResizing: false,
    enableColumnOrdering: false,
    ...(onSortingChange && {
      onSortingChange: (updater) => {
        const current = sorting ?? [];
        const next =
          typeof updater === 'function'
            ? updater(current)
            : updater;

        onSortingChange(next);
      },
    }),
    onCreatingRowCancel: () => {
      // Handle create row cancel
    },
    onCreatingRowSave: ({ values, table }) => {
      handleCreateRow(values as T);
      table.setCreatingRow(null);
    },
    ...(onGlobalSearch && {
      onGlobalFilterChange: async (query) => {
        await onGlobalSearch(String(query ?? ''));
      },
    }),
    ...(onPaginationChange && {
      onPaginationChange: (updater) => {
        const next = typeof updater === 'function' ? updater(table.getState().pagination) : updater;
        onPaginationChange({ pageIndex: next.pageIndex, pageSize: next.pageSize });
      },
    }),

    rowCount: pagination?.totalCount ?? data.length,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: '1px solid #e5e7eb',
        borderRadius: '0.375rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: '650px',
        borderRadius: '0.375rem',
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: '6px',
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f5f9',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#cbd5e1',
          borderRadius: '3px',
          '&:hover': {
            background: '#94a3b8',
          },
        },
        '&::-webkit-scrollbar-corner': {
          background: '#f1f5f9',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: hasUnsavedChanges(row) ? '#fef3c7' : 'inherit',
        '&:hover': {
          backgroundColor: hasUnsavedChanges(row) ? '#fde68a' : '#f8fafc',
        },
        '& .MuiTableRow-expandIconCell': {
          '& svg': {
            color: '#6b7280',
            fontSize: '1.25rem',
          },
        },
      },
    }),
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        backgroundColor: '#f8fafc',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: '#374151',
        borderBottom: '1px solid #e5e7eb',
        padding: '0.75rem 1rem',
        ...(column.id === 'mrt-row-actions' && {
          textAlign: 'center',
          minWidth: '120px',
          width: '120px',
          padding: '0.75rem 0.5rem',
        }),
      },
    }),
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        borderBottom: '1px solid #f3f4f6',
        padding: '0.75rem 1rem',
        fontSize: '0.875rem',
        color: '#1f2937',
        '&:hover': {
          backgroundColor: '#f8fafc',
        },
        ...(column.id === 'mrt-row-actions' && {
          padding: '0.5rem',
          textAlign: 'center',
          minWidth: '120px',
          width: '120px',
        }),
      },
    }),
    muiEditTextFieldProps: ({ cell, row }) => ({
      variant: 'outlined',
      size: 'small',
      onBlur: (event: any) => {
        const columnId = cell.column.id;
        const newValue = event.target.value;
        const rowId = row.id;

        // Update edited rows map
        setEditedRows(prev => {
          const newMap = new Map(prev);
          const existingRow = newMap.get(rowId) || { ...row.original };
          newMap.set(rowId, { ...existingRow, [columnId]: newValue });
          return newMap;
        });
      },
      sx: {
        '& .MuiOutlinedInput-root': {
          fontSize: '0.875rem',
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.875rem',
          color: '#6b7280',
        },
      },
    }),
    positionActionsColumn: 'last',
    enableRowActions: enableDelete || (enableDelete && isEditingEnabled) || !!renderCustomRowActions,
    enableExpandAll: !!renderDetailPanel,
    enableExpanding: !!renderDetailPanel,
    renderDetailPanel: renderDetailPanel ? ({ row }) => renderDetailPanel(row.original) : undefined,
    muiExpandButtonProps: ({ row }) => ({
      onClick: () => {
        // Don't prevent default - let MRT handle the expansion
        // Use requestAnimationFrame to detect the change after MRT updates its state
        requestAnimationFrame(() => {
          if (onExpandedChange) {
            const currentExpanded = table.getState().expanded as Record<string, boolean>;
            const rowId = row.id;
            const isExpanded = !!currentExpanded[rowId];
            onExpandedChange(row.original, isExpanded);
          }
        });
      },
      sx: {
        color: '#6b7280',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          color: '#374151',
        },
        '& svg': {
          fontSize: '1.25rem',
          transition: 'transform 0.2s ease-in-out',
        },
      },
    }),
    renderRowActions: ({ row }) => (
      <div className="flex items-center gap-1.5 h-full">
        {/* Custom Row Actions */}
        {renderCustomRowActions && renderCustomRowActions(row.original)}
        {/* Delete Button - Show when enableDelete is true */}
        {enableDelete && (
          <Button
            onClick={() => handleDeleteRow(row.original)}
            variant="ghost"
            size="sm"
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50"
            title="Delete row"
            aria-label="Delete row"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <div className="flex gap-2 items-center">
        {renderHeaderActions && renderHeaderActions()}

        {/* Add New Row Button */}
        {isEditingEnabled && onRowCreate && (
          <Button
            onClick={() => table.setCreatingRow(true)}
            variant="primary"
            size="sm"
            className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New
          </Button>
        )}

        {/* Save All Changes Button - Only show when there are edited rows */}
        {editedRows.size > 0 && (
          <>
            <Button
              onClick={handleSaveAllChanges}
              disabled={isSaving}
              variant="primary"
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save All Changes ({editedRows.size})
                </>
              )}
            </Button>
            {/* <Button
              onClick={handleDiscardChanges}
              disabled={isSaving}
              variant="secondary"
              size="sm"
            >
              <X className="w-4 h-4 mr-2" />
              Discard
            </Button> */}
          </>
        )}
      </div>
    ),
    state: {
      isLoading: loading ?? false,
      showAlertBanner: false,
      showProgressBars: false,
      // Only control pagination state if external handlers are provided
      ...(onPaginationChange && {
        pagination: {
          pageIndex: pagination?.pageIndex ?? 0,
          pageSize: pagination?.pageSize ?? 10,
        },
      }),
      // Only control filter state if external handler is provided
      ...(onGlobalSearch && {
        globalFilter: globalFilterValue ?? '',
      }),
      // Only control sorting state if external handler is provided
      ...(onSortingChange && {
        sorting: sorting ?? [],
      }),
    },
    muiLinearProgressProps: {
      sx: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
      },
    },
  });

  return (
    <section className={`animate-fadeIn ${className}`}>
      <MaterialReactTable table={table} />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title="Edit Row"
        size="lg"
        showCloseButton={true}
      >
        <div className="space-y-6">
          {rowToEdit && columns.map((column) => {
            if (!column.editable || column.key === 'actions') return null;

            const value = (rowToEdit as any)[column.key];
            const error = validationErrors[column.key];

            return (
              <div key={column.key}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {column.header}
                </label>
                {column.EditComponent ? (
                  <column.EditComponent
                    value={value}
                    onChange={() => {
                    }}
                    onBlur={() => {
                      // This will be handled by the hook in future versions
                    }}
                    error={error}
                  />
                ) : (
                  <input
                    type="text"
                    value={value || ''}
                    onChange={() => {
                    }}
                    onBlur={() => {
                      // This will be handled by the hook in future versions
                    }}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                  />
                )}
                {error && (
                  <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-3 justify-end pt-6 border-t border-gray-200">
          <Button
            onClick={closeEditModal}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
          >
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Save Changes
            </>
          </Button>
        </div>
      </Modal>
    </section>
  );
}

/**
 * Memoized EditableDataTable component for optimal performance
 */
export const EditableDataTable = memo(EditableDataTableComponent) as typeof EditableDataTableComponent;
